import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Hash } from 'lucide-react';

const KeywordSentimentMatrix = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [matrixData, setMatrixData] = useState({ matrix: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.4;

  useEffect(() => {
    fetchMatrixData();
  }, [filters]);

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/keyword-sentiment?limit=30`);
      const result = await response.json();
      setMatrixData(result);
    } catch (error) {
      console.error('Error fetching keyword sentiment:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment > 0.3) return '#10b981'; // Very positive
    if (sentiment > 0.1) return '#22c55e'; // Positive
    if (sentiment >= -0.1) return '#eab308'; // Neutral
    if (sentiment >= -0.3) return '#f97316'; // Negative
    return '#ef4444'; // Very negative
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Hash className="w-5 h-5 text-ink-500" />
          Keyword Sentiment Matrix
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Keywords ranked by sentiment • Bubble size = frequency
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Analyzing keywords...</div>
          </div>
        ) : matrixData.matrix.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No keyword data available</p>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap gap-3 items-center justify-center h-96 overflow-hidden">
              {matrixData.matrix.map((item, index) => {
                const size = Math.max(40, Math.min(120, item.mentions * 4));
                const color = getSentimentColor(item.avg_sentiment);
                
                return (
                  <motion.div
                    key={item.keyword}
                    className="rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      width: `${size}px`,
                      height: `${size}px`,
                      backgroundColor: color + '20',
                      border: `2px solid ${color}`
                    }}
                    initial={reducedMotion ? {} : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.03 }}
                    whileHover={reducedMotion ? {} : { scale: 1.1 }}
                    title={`${item.keyword}: ${item.mentions} mentions, sentiment ${item.avg_sentiment.toFixed(2)}`}
                  >
                    <div className="text-center px-2">
                      <p className="font-semibold text-xs" style={{ color }}>
                        {item.keyword}
                      </p>
                      <p className="text-[10px] text-slate-400">{item.mentions}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-6 flex items-center justify-center gap-2 text-xs flex-wrap">
              <span className="text-slate-400">Sentiment:</span>
              {[
                { color: '#ef4444', label: 'Very Negative' },
                { color: '#f97316', label: 'Negative' },
                { color: '#eab308', label: 'Neutral' },
                { color: '#22c55e', label: 'Positive' },
                { color: '#10b981', label: 'Very Positive' }
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-slate-400">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            {matrixData.meta && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{matrixData.matrix.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Top Keywords</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{matrixData.meta.unique_keywords || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Total Keywords</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{matrixData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KeywordSentimentMatrix;
